import React, { useState, useEffect } from 'react';
import {
  Layout,
  Button,
  Card,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  Space,
  message,
  Table,
  Tag,
  Select,
  DatePicker,
  Divider,
  Empty,
  Spin,
  Drawer
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import payrollService from '../services/payrollService';
import employeeService from '../services/employeeService';
import PayslipTemplateRenderer from '../components/PayslipTemplateRenderer';
import {
  canViewPayroll,
  canGeneratePayroll,
  canUpdatePayroll,
  canDownloadPayroll
} from '../utils/permissions';
import dayjs from 'dayjs';
import './PayrollManagement.css';

const PayrollManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const canView = canViewPayroll(user);
  const canGenerate = canGeneratePayroll(user);
  const canUpdate = canUpdatePayroll(user);
  const canDownload = canDownloadPayroll(user);

  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [generateForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [filters, setFilters] = useState({ month: null, year: null });

  useEffect(() => {
    if (canView) {
      loadPayslips();
      loadEmployees();
    }
  }, [canView]);

  const loadPayslips = async () => {
    setLoading(true);
    try {
      const response = await payrollService.getAll(pagination.page, pagination.limit, filters);
      setPayslips(response.data || []);
      setPagination({
        ...pagination,
        total: response.pagination?.total || 0
      });
    } catch (error) {
      message.error('Failed to load payslips');
      console.error('Load payslips error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeeService.getAll(1, 1000, { status: 'Active' });
      setEmployees(response?.data?.data || []);
    } catch (error) {
      console.error('Load employees error:', error);
    }
  };

  const handleGeneratePayslip = async (values) => {
    try {
      if (!values.employeeId || !values.month || !values.year) {
        message.error('Please fill all fields');
        return;
      }

      setLoading(true);
      await payrollService.generatePayslip(values.employeeId, values.month, values.year);
      message.success('Payslip generated successfully');
      setIsGenerateModalOpen(false);
      generateForm.resetFields();
      loadPayslips();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to generate payslip');
      console.error('Generate payslip error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (record) => {
    try {
      setLoading(true);
      const response = await payrollService.getPayslipWithDetails(record.id);
      setSelectedPayslip(response.data);
      setIsViewDrawerOpen(true);
    } catch (error) {
      message.error('Failed to load payslip details');
      console.error('View payslip error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayslip = async (record) => {
    try {
      setLoading(true);
      const response = await payrollService.getPayslipWithDetails(record.id);
      setSelectedPayslip(response.data);
      editForm.setFieldsValue({
        basicSalary: response.data.basicSalary,
        hra: response.data.hra,
        ta: response.data.ta,
        da: response.data.da,
        medicalAllowance: response.data.medicalAllowance,
        conveyanceAllowance: response.data.conveyanceAllowance,
        specialAllowance: response.data.specialAllowance,
        bonus: response.data.bonus,
        incentive: response.data.incentive,
        variablePay: response.data.variablePay,
        pf: response.data.pf,
        nps: response.data.nps,
        tds: response.data.tds,
        otherDeductions: response.data.otherDeductions
      });
      setIsEditDrawerOpen(true);
    } catch (error) {
      message.error('Failed to load payslip for editing');
      console.error('Edit payslip error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePayslip = async (values) => {
    try {
      setLoading(true);
      await payrollService.updatePayslip(selectedPayslip.id, values);
      message.success('Payslip updated successfully');
      setIsEditDrawerOpen(false);
      editForm.resetFields();
      loadPayslips();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update payslip');
      console.error('Update payslip error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPayslip = async (record) => {
    try {
      setLoading(true);
      const response = await payrollService.getPayslipWithDetails(record.id);
      setSelectedPayslip(response.data);
      const previousTitle = document.title;
      const downloadedAt = dayjs().format('YYYYMMDD_HHmmss');
      document.title = `payslip_${downloadedAt}`;

      const restoreTitle = () => {
        document.title = previousTitle;
        window.removeEventListener('afterprint', restoreTitle);
      };

      window.addEventListener('afterprint', restoreTitle);
      setTimeout(() => {
        window.print();
        setTimeout(restoreTitle, 1000);
      }, 100);
    } catch (error) {
      message.error('Failed to download payslip');
      console.error('Download payslip error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderPayslipPreview = () => {
    if (!selectedPayslip) return null;

    return (
      <div className="payslip-preview">
        <PayslipTemplateRenderer
          payslip={selectedPayslip}
          template={selectedPayslip.activeTemplate || selectedPayslip.template}
        />
      </div>
    );
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employee',
      key: 'employee',
      render: (employee) => (employee?.firstName ? `${employee.firstName} ${employee.lastName}` : 'N/A')
    },
    {
      title: 'Month',
      dataIndex: 'payrollMonth',
      key: 'payrollMonth',
      render: (date) => dayjs(date).format('MMMM YYYY')
    },
    {
      title: 'Gross Salary',
      dataIndex: 'grossSalary',
      key: 'grossSalary',
      render: (amount) => `Rs. ${parseFloat(amount).toFixed(2)}`
    },
    {
      title: 'Net Salary',
      dataIndex: 'netSalary',
      key: 'netSalary',
      render: (amount) => `Rs. ${parseFloat(amount).toFixed(2)}`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          Generated: 'blue',
          Approved: 'green',
          Paid: 'cyan',
          Cancelled: 'red'
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canView && (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPayslip(record)}
              title="View"
            />
          )}
          {canUpdate && record.status === 'Generated' && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditPayslip(record)}
              title="Edit"
            />
          )}
          {canDownload && (
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadPayslip(record)}
              title="Download"
            />
          )}
        </Space>
      )
    }
  ];

  if (!canView) {
    return <Empty description="You don't have permission to view payroll" />;
  }

  return (
    <Layout className="payroll-management" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Payroll Management</h1>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadPayslips}>
            Refresh
          </Button>
          {canGenerate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsGenerateModalOpen(true)}
            >
              Generate Payslip
            </Button>
          )}
        </Space>
      </div>

      <Card style={{ marginBottom: '20px' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Month">
              <DatePicker.MonthPicker
                format="YYYY-MM"
                onChange={(date) => {
                  if (date) {
                    setFilters({
                      ...filters,
                      month: date.month() + 1,
                      year: date.year()
                    });
                  } else {
                    setFilters({ month: null, year: null });
                  }
                }}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Button type="primary" block onClick={() => { setPagination({ ...pagination, page: 1 }); loadPayslips(); }}>
              Filter
            </Button>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <div className="payslip-print-area">
          {selectedPayslip && (
            <PayslipTemplateRenderer
              payslip={selectedPayslip}
              template={selectedPayslip.activeTemplate || selectedPayslip.template}
            />
          )}
        </div>

        <Table
          columns={columns}
          dataSource={payslips}
          rowKey="id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page) => {
              setPagination({ ...pagination, page });
            }
          }}
          loading={loading}
          style={{ marginBottom: '20px' }}
        />
      </Spin>

      {/* Generate Payslip Modal */}
      <Modal
        title="Generate Payslip"
        open={isGenerateModalOpen}
        onCancel={() => {
          setIsGenerateModalOpen(false);
          generateForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => setIsGenerateModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => generateForm.submit()}
          >
            Generate
          </Button>
        ]}
      >
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={handleGeneratePayslip}
        >
          <Form.Item
            label="Employee"
            name="employeeId"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              options={employees.map(emp => ({
                label: `${emp.firstName} ${emp.lastName}`,
                value: emp.id
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Month"
            name="month"
            rules={[{ required: true, message: 'Please select a month' }]}
          >
            <Select
              placeholder="Select month"
              options={[
                { label: 'January', value: 1 },
                { label: 'February', value: 2 },
                { label: 'March', value: 3 },
                { label: 'April', value: 4 },
                { label: 'May', value: 5 },
                { label: 'June', value: 6 },
                { label: 'July', value: 7 },
                { label: 'August', value: 8 },
                { label: 'September', value: 9 },
                { label: 'October', value: 10 },
                { label: 'November', value: 11 },
                { label: 'December', value: 12 }
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Year"
            name="year"
            rules={[{ required: true, message: 'Please enter a year' }]}
          >
            <InputNumber min={2020} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Payslip Drawer */}
      <Drawer
        title="Edit Payslip"
        placement="right"
        onClose={() => {
          setIsEditDrawerOpen(false);
          editForm.resetFields();
        }}
        open={isEditDrawerOpen}
        width={500}
        footer={
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <Space>
              <Button onClick={() => setIsEditDrawerOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={loading}
                onClick={() => editForm.submit()}
              >
                Save
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdatePayslip}
        >
          <Divider>Earnings</Divider>
          <Form.Item label="Basic Salary" name="basicSalary">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="HRA" name="hra">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="TA" name="ta">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="DA" name="da">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Medical Allowance" name="medicalAllowance">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Conveyance Allowance" name="conveyanceAllowance">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Special Allowance" name="specialAllowance">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Bonus" name="bonus">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Incentive" name="incentive">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Variable Pay" name="variablePay">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Divider>Deductions</Divider>
          <Form.Item label="PF" name="pf">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="NPS" name="nps">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="TDS" name="tds">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Other Deductions" name="otherDeductions">
            <InputNumber precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* View Payslip Drawer */}
      <Drawer
        title="Payslip Preview"
        placement="right"
        onClose={() => setIsViewDrawerOpen(false)}
        open={isViewDrawerOpen}
        width={600}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setIsViewDrawerOpen(false)}>Close</Button>
          </div>
        }
      >
        {renderPayslipPreview()}
      </Drawer>
    </Layout>
  );
};

export default PayrollManagement;
